import React, { useState, useEffect } from "react";
import {
  Copy,
  Key,
  Eye,
  EyeOff,
  Trash2,
  Globe,
  Clock,
  Plus,
  Radio,
  Bot,
  Terminal,
  Check,
  Zap,
  Info
} from "lucide-react";
import ApiClient from "../utils/api";
import DeleteConfirmModal from "../components/DeleteConfirmModal";
import { useToast } from "../context/ToastContext";

const WebhookManagement = ({ activeAiAgentId }) => {
  const { showToast } = useToast();
  const [apiKeys, setApiKeys] = useState([]);
  const [newKeyName, setNewKeyName] = useState("");
  const [showKeys, setShowKeys] = useState({});
  const [copiedUrl, setCopiedUrl] = useState(false);
  const [copiedKeyId, setCopiedKeyId] = useState(null);
  const [copiedCurlId, setCopiedCurlId] = useState(null);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [keyToDelete, setKeyToDelete] = useState(null);
  const [isDeleting, setIsDeleting] = useState(false);
  const [isCreating, setIsCreating] = useState(false);

  useEffect(() => {
    loadApiKeys();
  }, [activeAiAgentId]);

  const loadApiKeys = async () => {
    try {
      const response = await ApiClient.request("/webhook/keys");
      setApiKeys(response.keys || []);
    } catch (error) {
      console.error("Failed to load API keys:", error);
    }
  };

  const createApiKey = async () => {
    if (!newKeyName.trim() || isCreating) return;
    setIsCreating(true);
    try {
      await ApiClient.request("/webhook/keys", {
        method: "POST",
        body: JSON.stringify({ key_name: newKeyName.trim() }),
      });

      showToast(`API Key "${newKeyName.trim()}" created successfully!`, "success");
      setNewKeyName("");
      loadApiKeys();
    } catch (error) {
      showToast(`Failed: ${error.message || error}`, "error");
    } finally {
      setIsCreating(false);
    }
  };

  const toggleKey = async (keyId, currentStatus) => {
    try {
      await ApiClient.request(`/webhook/keys/${keyId}`, {
        method: "PUT",
        body: JSON.stringify({ is_active: !currentStatus }),
      });
      loadApiKeys();
      showToast("Status updated");
    } catch (error) {
      showToast("Failed to toggle API key", "error");
    }
  };

  const toggleStreaming = async (keyId, currentStreamingStatus) => {
    try {
      await ApiClient.request(`/webhook/keys/${keyId}/streaming`, {
        method: "PUT",
        body: JSON.stringify({ streaming_enabled: !currentStreamingStatus }),
      });
      loadApiKeys();
      showToast("Streaming setting updated");
    } catch (error) {
      showToast("Failed to toggle streaming", "error");
    }
  };

  const toggleAutoReply = async (keyId, currentAutoReplyStatus) => {
    try {
      await ApiClient.request(`/webhook/keys/${keyId}/auto-reply`, {
        method: "PUT",
        body: JSON.stringify({ auto_reply: !currentAutoReplyStatus }),
      });
      loadApiKeys();
      showToast("Auto-reply setting updated");
    } catch (error) {
      showToast("Failed to toggle auto-reply", "error");
    }
  };

  const deleteKey = (keyId) => {
    setKeyToDelete(keyId);
    setIsDeleteModalOpen(true);
  };

  const confirmDelete = async () => {
    if (!keyToDelete) return;
    setIsDeleting(true);
    try {
      await ApiClient.request(`/webhook/keys/${keyToDelete}`, {
        method: "DELETE",
      });
      loadApiKeys();
      showToast("API key permanently deleted", "success");
      setIsDeleteModalOpen(false);
    } catch (error) {
      showToast("Failed to delete API key", "error");
    } finally {
      setIsDeleting(false);
    }
  };

  const copyToClipboard = (text, type = "key", id = null) => {
    navigator.clipboard.writeText(text).then(() => {
      if (type === "url") {
        setCopiedUrl(true);
        setTimeout(() => setCopiedUrl(false), 2000);
      } else if (type === "curl") {
        setCopiedCurlId(id);
        setTimeout(() => setCopiedCurlId(null), 2000);
      } else if (type === "key") {
        setCopiedKeyId(id);
        setTimeout(() => setCopiedKeyId(null), 2000);
      }
      const label = type === "url" ? "URL" : type === "curl" ? "cURL command" : "API Key";
      showToast(`${label} copied to clipboard!`);
    });
  };

  const webhookUrl = `${process.env.REACT_APP_API_BASE_URL}/webhook/incoming`;

  return (
    <div className="flex flex-col min-h-screen bg-white font-['Inter',sans-serif] animate-slideIn">
      <div className="w-full max-w-7xl mx-auto px-4 py-5 sm:px-6 sm:py-6 lg:px-8 space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center justify-between pb-5 sm:pb-6 border-b border-zinc-100 gap-3 sm:gap-4">
          <div>
            <div className="flex items-center gap-2.5">
              <h1 className="text-lg sm:text-xl font-bold text-zinc-900 tracking-tight">
                Webhooks & API Keys
              </h1>
            </div>
            <p className="text-xs text-zinc-500 mt-0.5 sm:mt-1">
              Securely stream messages and trigger incoming real-time workflows via HTTP requests.
            </p>
          </div>
          <div className="flex items-center gap-2 self-start sm:self-auto">
            <span className="text-[11px] sm:text-xs text-zinc-500 font-medium px-2.5 py-1 bg-zinc-50 border border-zinc-200 rounded-lg shadow-2xs">
              {apiKeys.length} {apiKeys.length === 1 ? "key active" : "keys active"}
            </span>
          </div>
        </div>

        {/* Universal Endpoint Box */}
        <div className="bg-white rounded-xl border border-zinc-200 p-3.5 sm:p-5">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2.5 mb-3">
            <div className="flex items-center gap-2.5">
              <Globe className="text-zinc-800 shrink-0" size={18} />
              <div>
                <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Universal Endpoint</h3>
                <p className="text-[11px] text-zinc-500">Destination URL for incoming POST event payloads</p>
              </div>
            </div>
            <span className="self-start sm:self-auto text-[10px] font-mono px-2 py-0.5 bg-blue-50 text-blue-700 border border-blue-200 rounded font-medium">
              POST
            </span>
          </div>

          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-2 p-1.5 sm:p-2 bg-zinc-50 rounded-lg border border-zinc-200">
            <div className="font-mono text-[11px] sm:text-xs text-zinc-700 px-2 py-1 flex-1 break-all select-all min-w-0">
              {webhookUrl}
            </div>
            <button
              onClick={() => copyToClipboard(webhookUrl, "url")}
              className={`inline-flex items-center justify-center gap-1.5 px-3 py-1.5 text-xs font-medium rounded-md transition-colors flex-shrink-0 ${
                copiedUrl
                  ? "bg-emerald-50 text-emerald-700 border border-emerald-200"
                  : "bg-white text-zinc-800 border border-zinc-200 hover:bg-zinc-100"
              }`}
              title={copiedUrl ? "Copied!" : "Copy URL"}
            >
              {copiedUrl ? <Check size={13} className="text-emerald-600" /> : <Copy size={13} />}
            </button>
          </div>

          <div className="mt-3 flex items-start gap-2 text-[11px] text-zinc-500">
            <Info size={13} className="text-zinc-400 mt-0.5 flex-shrink-0" />
            <p className="leading-relaxed">
              Include <code className="text-zinc-800 bg-zinc-100 px-1 py-0.5 rounded text-[10px] font-mono break-all">Authorization: Bearer YOUR_API_KEY</code> and <code className="text-zinc-800 bg-zinc-100 px-1 py-0.5 rounded text-[10px] font-mono">Content-Type: application/json</code> in your HTTP headers.
            </p>
          </div>
        </div>

        {/* Generate API Key Card */}
        <div className="bg-white rounded-xl border border-zinc-200 p-3.5 sm:p-5">
          <div className="flex items-center gap-2.5 mb-3">
            <Key className="text-zinc-800 shrink-0" size={18} />
            <div>
              <h3 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider">Generate API Key</h3>
              <p className="text-[11px] text-zinc-500">Create scoped authorization credentials for services & integrations</p>
            </div>
          </div>

          <div className="flex flex-col sm:flex-row gap-2.5 items-stretch sm:items-center">
            <div className="flex-1">
              <input
                type="text"
                placeholder="Service or integration name (e.g. Stripe, Shopify, Internal CRM)"
                value={newKeyName}
                onChange={(e) => setNewKeyName(e.target.value)}
                onKeyDown={(e) => e.key === "Enter" && createApiKey()}
                className="w-full px-3 py-2 text-xs border border-zinc-200 rounded-lg bg-zinc-50/50 focus:bg-white focus:outline-none focus:border-zinc-900 text-zinc-900 placeholder:text-zinc-400 font-medium transition-colors"
              />
            </div>
            <button
              onClick={createApiKey}
              disabled={!newKeyName.trim() || isCreating}
              className="inline-flex items-center justify-center gap-1.5 px-4 py-2 bg-zinc-900 text-white rounded-lg hover:bg-zinc-800 disabled:opacity-40 text-xs font-medium transition-colors flex-shrink-0 w-full sm:w-auto"
            >
              {isCreating ? (
                <div className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
              ) : (
                <Plus size={14} />
              )}
              <span>{isCreating ? "Generating..." : "Create Key"}</span>
            </button>
          </div>
        </div>

        {/* API Keys List */}
        <div className="space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-xs font-semibold text-zinc-900 uppercase tracking-wider flex items-center gap-2">
              <Zap size={14} className="text-zinc-500" />
              <span>Registered API Keys ({apiKeys.length})</span>
            </h2>
          </div>

          {apiKeys.length === 0 ? (
            <div className="text-center py-10 sm:py-12 bg-white rounded-xl border border-zinc-200 p-5 sm:p-6">
              <h4 className="text-xs font-semibold text-zinc-900">No API keys yet</h4>
              <p className="text-[11px] text-zinc-400 mt-0.5">
                Generate your first credential key above to start receiving webhook payloads.
              </p>
            </div>
          ) : (
            <div className="space-y-3">
              {apiKeys.map((key) => {
                const isShowing = !!showKeys[key.id];
                const isKeyCopied = copiedKeyId === key.id;
                const isCurlCopied = copiedCurlId === key.id;

                const curlPayload = `curl -X POST "${webhookUrl}" \\\n  -H "Content-Type: application/json" \\\n  -H "Authorization: Bearer ${key.api_key}" \\\n  -d '{\n    "message": "Hello from webhook",\n    "session_id": ""\n  }'`;

                return (
                  <div
                    key={key.id}
                    className="bg-white rounded-xl border border-zinc-200 p-3.5 sm:p-5 transition-all hover:border-zinc-300 space-y-3 sm:space-y-4"
                  >
                    {/* Top Row: Name, Created Date, Status Badge, and Main Active Switch */}
                    <div className="flex items-center justify-between gap-3 pb-3 border-b border-zinc-100">
                      <div className="flex items-center gap-2.5 sm:gap-3 min-w-0">
                        <Key size={16} className={key.is_active ? "text-zinc-900 shrink-0" : "text-zinc-400 shrink-0"} />
                        <div className="min-w-0">
                          <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap">
                            <h4 className="text-xs font-semibold text-zinc-900 truncate max-w-[140px] sm:max-w-xs">{key.key_name}</h4>
                            <span className={`px-1.5 sm:px-2 py-0.2 rounded text-[10px] font-medium border ${
                              key.is_active
                                ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                                : "bg-zinc-50 text-zinc-500 border-zinc-200"
                            }`}>
                              {key.is_active ? "Active" : "Inactive"}
                            </span>
                          </div>
                          <div className="flex items-center gap-1 text-[10px] sm:text-[11px] text-zinc-400 mt-0.5">
                            <Clock size={11} />
                            <span>Created {new Date(key.created_at).toLocaleDateString()}</span>
                          </div>
                        </div>
                      </div>

                      {/* Main Enable / Disable toggle switch */}
                      <div className="flex items-center gap-1.5 sm:gap-2 flex-shrink-0">
                        <button
                          type="button"
                          onClick={() => toggleKey(key.id, key.is_active)}
                          className={`w-8 h-4.5 rounded-full transition-colors relative flex-shrink-0 ${
                            key.is_active ? "bg-zinc-900" : "bg-zinc-200"
                          }`}
                          title={key.is_active ? "Deactivate Key" : "Activate Key"}
                        >
                          <div
                            className={`absolute top-0.5 w-3.5 h-3.5 bg-white rounded-full transition-transform ${
                              key.is_active ? "left-4" : "left-0.5"
                            }`}
                          />
                        </button>
                        <button
                          onClick={() => deleteKey(key.id)}
                          className="p-1.5 text-zinc-400 hover:text-red-600 rounded-md transition-colors"
                          title="Delete API key"
                        >
                          <Trash2 size={15} />
                        </button>
                      </div>
                    </div>

                    {/* Middle Row: Secret Key & Action Buttons */}
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-2.5 sm:gap-3">
                      {/* Key Box */}
                      <div className="flex-1 flex items-center gap-1.5 p-1 sm:p-1.5 bg-zinc-50/70 rounded-lg border border-zinc-200 min-w-0">
                        <div className={`font-mono text-[11px] sm:text-xs text-zinc-700 px-2 py-1 flex-1 select-all ${
                          isShowing ? "break-all" : "truncate"
                        }`}>
                          {isShowing ? key.api_key : "••••••••••••••••••••••••••••••••"}
                        </div>
                        <button
                          type="button"
                          onClick={() => setShowKeys(prev => ({ ...prev, [key.id]: !prev[key.id] }))}
                          className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-200/60 rounded-md transition-colors flex-shrink-0"
                          title={isShowing ? "Hide API Key" : "Reveal API Key"}
                        >
                          {isShowing ? <EyeOff size={14} /> : <Eye size={14} />}
                        </button>
                        <button
                          type="button"
                          onClick={() => copyToClipboard(key.api_key, "key", key.id)}
                          className="p-1.5 text-zinc-400 hover:text-zinc-700 hover:bg-zinc-200/60 rounded-md transition-colors flex-shrink-0"
                          title="Copy API key"
                        >
                          {isKeyCopied ? <Check size={14} className="text-emerald-600" /> : <Copy size={14} />}
                        </button>
                      </div>

                      {/* Feature Controls (Stream, Auto-Reply, cURL snippet) */}
                      <div className="flex items-center gap-2 flex-wrap sm:flex-nowrap">
                        {/* Streaming Toggle */}
                        <div className="flex-1 sm:flex-initial flex items-center justify-between sm:justify-start gap-2 px-2.5 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg">
                          <div className="flex items-center gap-1.5">
                            <Radio size={12} className={key.streaming_enabled ? "text-zinc-900" : "text-zinc-400"} />
                            <span className="text-[11px] sm:text-xs font-medium text-zinc-700">Stream</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleStreaming(key.id, key.streaming_enabled)}
                            className={`w-7 h-4 rounded-full transition-colors relative flex-shrink-0 ${
                              key.streaming_enabled ? "bg-zinc-900" : "bg-zinc-200"
                            }`}
                            title={key.streaming_enabled ? "Disable Streaming" : "Enable Streaming"}
                          >
                            <div
                              className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                                key.streaming_enabled ? "left-3.5" : "left-0.5"
                              }`}
                            />
                          </button>
                        </div>

                        {/* Auto Reply Toggle */}
                        <div className="flex-1 sm:flex-initial flex items-center justify-between sm:justify-start gap-2 px-2.5 py-1.5 bg-zinc-50 border border-zinc-200 rounded-lg">
                          <div className="flex items-center gap-1.5">
                            <Bot size={12} className={key.auto_reply ? "text-zinc-900" : "text-zinc-400"} />
                            <span className="text-[11px] sm:text-xs font-medium text-zinc-700">Auto Reply</span>
                          </div>
                          <button
                            type="button"
                            onClick={() => toggleAutoReply(key.id, key.auto_reply)}
                            className={`w-7 h-4 rounded-full transition-colors relative flex-shrink-0 ${
                              key.auto_reply ? "bg-zinc-900" : "bg-zinc-200"
                            }`}
                            title={key.auto_reply ? "Disable Auto-Reply" : "Enable Auto-Reply"}
                          >
                            <div
                              className={`absolute top-0.5 w-3 h-3 bg-white rounded-full transition-transform ${
                                key.auto_reply ? "left-3.5" : "left-0.5"
                              }`}
                            />
                          </button>
                        </div>

                        {/* cURL Command Copy */}
                        <button
                          type="button"
                          onClick={() => copyToClipboard(curlPayload, "curl", key.id)}
                          className={`inline-flex items-center justify-center gap-1.5 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors flex-shrink-0 ${
                            isCurlCopied
                              ? "bg-emerald-50 text-emerald-700 border-emerald-200"
                              : "bg-white text-zinc-700 border-zinc-200 hover:bg-zinc-50"
                          }`}
                          title="Copy cURL snippet for terminal testing"
                        >
                          {isCurlCopied ? <Check size={12} className="text-emerald-600" /> : <Terminal size={12} />}
                          <span>{isCurlCopied ? "Copied" : "cURL"}</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      </div>

      <DeleteConfirmModal
        isOpen={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
        onConfirm={confirmDelete}
        isLoading={isDeleting}
        title="Delete API Key?"
        message="This action will permanently revoke this API key. Any external service using this credential will no longer be able to deliver webhook payloads."
      />
    </div>
  );
};

export default WebhookManagement;
